import config from 'config';
import { action, autorun, computed, observable } from 'mobx';
import MediahubStore from 'stores/MediahubStore';
// Import other stores
import ViewStore from 'stores/ViewStore';
// Import API
import API from 'utils/api';

import { getLinkFromChannelTitle, getMetadata, getPreviewImage } from 'utils/helpers';
// Import the MediaContainer Class
import MediaContainer from './MediaContainer';

/**
 * Channel Class
 */
export default class Channel {
  // Data loading
  @observable pendingRequestCount = 0;

  // Channel Data
  @observable id;
  @observable metadata;
  @observable previewImages;
  @observable mostViewed;

  @observable teasers     = [];
  @observable channelSize = 0;

  /**
   * CONSTRUCTOR
   */
  constructor(channel, mostViewed) {
    this.id            = _.result(channel, 'channelId', null);
    this.metadata      = _.result(channel, 'metadata', []);
    this.previewImages = _.result(channel, 'previewImages', []);
    this.mostViewed    = mostViewed;

    autorun(() => {
      const currentChannel  = MediahubStore.currentChannel;
      const currentViewName = ViewStore.currentViewName;

      if(!this.hasLoadedInitialData && (_.isEqual(this.id, currentChannel.id) || _.isEqual(currentViewName, 'overview'))) {
        this.loadTeasers();
      }
    });
  }

  /**
   * COMPUTED VALUES
   */

  @computed
  get title() {
    return getMetadata(this.metadata, 'channel_name', ViewStore.language);
  }

  @computed
  get link() {
    return getLinkFromChannelTitle(this.title);
  }

  @computed
  get link_en() {
    return getLinkFromChannelTitle(getMetadata(this.metadata, 'channel_name', 'en'));
  }

  @computed
  get link_de() {
    return getLinkFromChannelTitle(getMetadata(this.metadata, 'channel_name', 'de'));
  }

  @computed
  get description() {
    return getMetadata(this.metadata, 'channel_description', ViewStore.language);
  }

  @computed
  get thumbnail() {
    return getPreviewImage(this.previewImages, 260);
  }

  @computed
  get hasLoadedInitialData() {
    return this.teasers.length;
  }

  /**
   * ACTIONS
   */
  @action
  loadTeasers() {
    this.pendingRequestCount++;

    API.post('search', {
      data: {
        token: API.token,
        query: { bool: { filter: [{ terms: { channelId: [this.id] } }] } },
        size: 256,
        sort: [
          {
            'links.sortCriteria': {
              order: 'desc',
              nested: { path: 'links', filter: { term: { 'links.link': 'dQSzY8CWrMPUyQUbXhFnNA' } } },
            },
          },
          { 'links.sortCriteria': { order: 'desc', nested: { path: 'links', filter: { term: { 'links.channelId': this.id } } } } },
        ],
      },
    })
      .then((result) => this.loadTeasersSuccess(result))
      .fail((err) => this.loadError('search 3', err));
  }

  @action
  loadError(request, error) {
    this.pendingRequestCount--;
    console.warn(`loadError ${request}`, error);
  }


  // TODO -> abstract this whole block cause it's in 3 places
  // from loadTeasersSuccess till deliverThumbsSuccess
  @action
  loadTeasersSuccess(result) {
    this.pendingRequestCount--;
    let channelMediaContainerIds = [];

    this.channelSize = result.numberOfHits;
    const ids = _.map(result.hits, 'mediaContainerId');

    _.forEach(result.hits, (mc) => {
      const mediaContainer = new MediaContainer(mc);
      this.teasers.push(mediaContainer);
      channelMediaContainerIds.push({
        mediaContainerId: mc.mediaContainerId,
        channelId:        mc.channelId,
      });

    });

    if(_.size(ids)) {
      this.deliverThumbs({ channelMediaContainerIds });
    }
  }

  @action
  deliverThumbs(data) {
    this.pendingRequestCount++;

    data['previewImages'] = true;
    data['width']         = 480;

    if(config.includeAuthToken) {
      data['token'] = API.token;
    }

    API.post('deliverThumbs', { data }) // TODO add auth ???
      .then((result) => this.deliverThumbsSuccess(result))
      .fail((err) => this.loadError('deliverThumbs', err));
  }

  @action
  deliverThumbsSuccess(thumbs) {
    this.pendingRequestCount--;
    const thumbsByMc = _.groupBy(thumbs, 'mediaContainerId');

    _.forEach(thumbsByMc, (value, key) => {
      const mcs = _.filter(this.teasers, { id: _.toInteger(key) });
      _.forEach(mcs, (mc) => {
        mc.setThumbnails(value);
      });
    });
  }
}
